import { useCallback, useEffect, useRef, useState } from "react";
import api from "../utils/apiClient";
import { getStoredAuthToken } from "../utils/restoreAuthSession";

export type InactivityPolicy = {
  enabled: boolean;
  timeoutMinutes: number;
  warningSeconds: number;
};

/** MITO, Trust and BasketMouth are all widget tenants as far as the policy goes. */
const SITE_GROUP = "widget";

const FALLBACK_POLICY: InactivityPolicy = {
  enabled: true,
  timeoutMinutes: 15,
  warningSeconds: 30,
};

/** Shared so a member active in one tab is not signed out from another. */
export const LAST_ACTIVITY_KEY = "mito.lastActivityAt";

const ACTIVITY_EVENTS = [
  "mousedown",
  "pointerdown",
  "keydown",
  "touchstart",
  "click",
  "scroll",
  "wheel",
] as const;

/** Writing on every mouse move would thrash storage for no benefit. */
const ACTIVITY_WRITE_INTERVAL_MS = 5_000;
const TICK_MS = 1_000;

export const clearLastActivityAt = () => {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    // ignore
  }
};

export const bumpLastActivityAt = (at: number = Date.now()) => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    // Single-tab behaviour still works without the shared timestamp.
  }
};

const readLastActivity = (): number => {
  try {
    const raw = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  } catch {
    return 0;
  }
};

type Options = {
  /** Runs once the countdown reaches zero, before the session is cleared. */
  onTimeout: () => void;
};

/**
 * Signs a member out after a period without interaction, warning them first.
 * The timeout and countdown are set by MasterAdmin, so every site agrees.
 */
const useInactivityTimeout = ({ onTimeout }: Options) => {
  const [policy, setPolicy] = useState<InactivityPolicy | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [warning, setWarning] = useState(false);

  const lastWriteRef = useRef(0);
  const firedRef = useRef(false);
  const warningRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  warningRef.current = warning;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response: any = await api.get(
          `Session/InactivityPolicy?siteGroup=${SITE_GROUP}`
        );
        const data = response?.data?.data ?? response?.data;
        if (cancelled || !data) {
          if (!cancelled) setPolicy(FALLBACK_POLICY);
          return;
        }
        // A zero timeout is how MasterAdmin switches sign-out off for this group.
        const timeoutMinutes = Number(data.timeoutMinutes);
        setPolicy({
          enabled: data.enabled !== false && timeoutMinutes > 0,
          timeoutMinutes: timeoutMinutes || FALLBACK_POLICY.timeoutMinutes,
          warningSeconds:
            Number(data.warningSeconds) || FALLBACK_POLICY.warningSeconds,
        });
      } catch {
        // An unreachable policy must not leave a session open forever.
        if (!cancelled) setPolicy(FALLBACK_POLICY);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const markActive = useCallback(() => {
    const now = Date.now();
    // While the warning modal is up, every interaction must restart the clock
    // immediately — the 5s throttle would make the countdown look stuck.
    if (
      !warningRef.current &&
      now - lastWriteRef.current < ACTIVITY_WRITE_INTERVAL_MS
    ) {
      return;
    }
    lastWriteRef.current = now;
    bumpLastActivityAt(now);
    if (warningRef.current) {
      firedRef.current = false;
      setWarning(false);
    }
  }, []);

  /** "Stay signed in" — treat the click as fresh activity and drop the warning. */
  const staySignedIn = useCallback(() => {
    const now = Date.now();
    lastWriteRef.current = now;
    bumpLastActivityAt(now);
    firedRef.current = false;
    setWarning(false);
  }, []);

  useEffect(() => {
    if (!policy?.enabled) return;
    if (!getStoredAuthToken()) return;

    // Fresh signed-in mount must start the clock from now. A leftover timestamp
    // from a previous tab/session would otherwise fire an instant timeout.
    const now = Date.now();
    lastWriteRef.current = now;
    bumpLastActivityAt(now);
    firedRef.current = false;
    setWarning(false);

    const handler = () => markActive();
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handler, { passive: true })
    );
    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handler)
      );
    };
  }, [policy, markActive]);

  useEffect(() => {
    if (!policy?.enabled) return;

    const totalMs = policy.timeoutMinutes * 60_000;
    const warnMs = policy.warningSeconds * 1_000;

    const tick = () => {
      if (!getStoredAuthToken()) {
        setWarning(false);
        return;
      }

      const last = readLastActivity() || Date.now();
      const remainingMs = totalMs - (Date.now() - last);

      if (remainingMs <= 0) {
        if (firedRef.current) return;
        firedRef.current = true;
        setWarning(false);
        onTimeoutRef.current();
        return;
      }

      if (remainingMs <= warnMs) {
        setWarning(true);
        setSecondsLeft(Math.ceil(remainingMs / 1000));
      } else {
        setWarning(false);
      }
    };

    tick();
    const id = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(id);
  }, [policy]);

  return {
    isWarning: warning,
    secondsLeft,
    staySignedIn,
    warningSeconds: policy?.warningSeconds ?? FALLBACK_POLICY.warningSeconds,
  };
};

export default useInactivityTimeout;
